/**
 * FI-2811: where keyboard focus goes once a soft navigation has put a new page on screen.
 *
 * Both soft paths (`DocumentSwap.swapDocumentContent` and the client-route mount in `EnhancedNavigation`) replace the
 * navigation root's children, and the clicked link was one of them, so focus fell to `<body>`: a keyboard member
 * restarted Tab from the top of the document and a screen reader announced nothing (the progress bar is
 * `aria-hidden`). The in-page anchor scroll already focuses its destination (`InPageAnchorScroll`, LANDING-44); this
 * is the cross-page twin. Design: enhanced-navigation design §7.7.
 *
 * Only STRANDED focus moves (`<body>`, none, or a node no longer in the document). Focus on the persistent chrome (the
 * header's search box, a header link) or on something the new page focused itself (a composer's autofocus) is left
 * where it is. Never called on a pop, which is restoring a scroll offset.
 */
const NAVIGATION_ROOT_SELECTOR = '[data-navigation-root="main-content"]';

/** An island container: `createRoot` discards its server children, so a heading inside it is about to be replaced. */
const ISLAND_CONTAINER_SELECTOR = "[data-component]";

function isFocusStranded(): boolean {
    const active = document.activeElement;
    return !active || active === document.body || !active.isConnected;
}

/** The first `h1` of the new page that survives the island mount, else the navigation root itself. */
function resolveFocusTarget(root: HTMLElement): HTMLElement {
    const headings = Array.from<HTMLElement>(root.querySelectorAll<HTMLElement>("h1"));
    const heading = headings.find((candidate) => candidate.closest(ISLAND_CONTAINER_SELECTOR) === null);
    return heading ?? root;
}

export function moveFocusToNavigatedPage(root?: HTMLElement | null): void {
    if (typeof document === "undefined") {
        return;
    }
    const navigationRoot = root ?? document.querySelector<HTMLElement>(NAVIGATION_ROOT_SELECTOR);
    if (!navigationRoot || !navigationRoot.isConnected || !isFocusStranded()) {
        return;
    }
    const target = resolveFocusTarget(navigationRoot);
    try {
        if (!target.hasAttribute("tabindex")) {
            // Programmatic focus only: `-1` is never a Tab stop, and it leaves again with the focus.
            target.setAttribute("tabindex", "-1");
            const isRootFallback = target === navigationRoot;
            if (isRootFallback) {
                // A ring around the whole page content says nothing; the landmark is what a screen reader announces.
                target.style.outline = "none";
            }
            target.addEventListener("blur", () => {
                target.removeAttribute("tabindex");
                if (isRootFallback) {
                    target.style.removeProperty("outline");
                }
            }, { once: true });
        }
        // The runtime has just set the scroll (top for a push or replace); focusing must not move it.
        target.focus({ preventScroll: true });
    } catch {
        // Focus is an enhancement here: a failure must not turn a settled navigation into a failed one.
    }
}
